<template>
  <p>{{ $t("节点配置") }}</p>
  <!-- 编辑 branches -->
  <t-form-item name="tip" :rules="requireRule">
    <template #label>
      <text-with-mark :text="$t('引导话术')" />
    </template>
    <t-input
      v-model="formData.tip"
      maxlength="200"
      :placeholder="$t('例如：请选择')"
    />
  </t-form-item>

  <t-form-item name="type">
    <template #label>
      <text-with-mark :text="$t('选项类型')" />
    </template>
    <t-radio-group v-model="formData.optionType">
      <t-radio value="1">
        {{ $t("固定") }}
        <t-popup
          :content="$t('用户点击后不会消失，可重复点击，用于持续导航')"
          show-arrow
          destroy-on-close
        >
          <InfoCircleIcon class="infoIcon" />
        </t-popup>
      </t-radio>
      <t-radio value="0">
        {{ $t("一次性") }}
        <t-popup
          show-arrow
          :content="$t('用户点击或发送任意消息后消失，用于即时反馈')"
          destroy-on-close
        >
          <InfoCircleIcon class="infoIcon" />
        </t-popup>
      </t-radio>
    </t-radio-group>
  </t-form-item>

  <p style="margin: 14px 0">
    <text-with-mark :text="$t('选项配置')" />
  </p>

  <div>
    <t-table
      class="no-border-table"
      :data="formData.branches"
      row-key="id"
      drag-sort="row-handler"
      lazy-load
      size="small"
      :columns="columns"
      vertical-align="top"
      :show-header="false"
      @dragSort="onDragSort"
    >
      <template #branch="{ rowIndex }">
        <t-form-item
          :name="`branches[${rowIndex}].content`"
          :rules="requireRule"
          :required-mark="false"
        >
          <t-space :size="8">
            <t-input
              v-model="formData.branches[rowIndex].content"
              maxlength="200"
              style="width: 350px"
            />
            <t-popup show-arrow trigger="click" placement="top-right">
              <LinkIcon
                size="16"
                :style="{
                  cursor: 'pointer',
                  verticalAlign: 'bottom',
                  color: getLinkColor(rowIndex),
                }"
              />
              <template #content>
                <div v-if="formData.optionType === '1'" class="popContainer">
                  <div>
                    <t-form-item
                      :name="`branches[${rowIndex}].urlStatus`"
                      :required-mark="false"
                      style="margin-bottom: 0"
                    >
                      <span>{{ $t("跳转链接") }}</span>
                      <t-switch
                        :value="!!formData.branches[rowIndex].urlStatus"
                        style="margin-left: 4px"
                        @change="onSwitchChange(rowIndex, $event)"
                      />
                    </t-form-item>
                  </div>
                  <div style="padding-top: 4px">
                    <span style="color: #00000066">
                      {{ $t("开启后，用户点击此选项将跳转至输入的链接") }}
                    </span>
                  </div>
                  <t-form-item
                    v-if="formData.branches[rowIndex].urlStatus"
                    :name="`branches[${rowIndex}].url`"
                    :rules="urlRule(formData.branches[rowIndex].urlStatus)"
                    :required-mark="false"
                  >
                    <t-input
                      v-model="formData.branches[rowIndex].url"
                      placeholder="https://"
                      style="padding-top: 12px"
                    />
                  </t-form-item>
                </div>
                <div v-else>
                  {{ $t("请将选项类型改为固定，再使用跳转链接功能") }}
                </div>
              </template>
            </t-popup>
            <CloseCircleIcon
              size="16"
              style="cursor: pointer; vertical-align: bottom"
              @click="deleteRelation(rowIndex)"
            />
          </t-space>
        </t-form-item>
      </template>
      <template #bottomContent>
        <t-form-item
          name="branches"
          :rules="branchListRules"
          :status-icon="false"
        >
          <div v-if="formData.branches.length < 20">
            <t-link theme="primary" hover="color" @click="addRelation">{{
              $t("添加选项")
            }}</t-link>
          </div>
        </t-form-item>
      </template>
    </t-table>
  </div>
</template>

<script lang="tsx" setup>
import { reactive, watch } from "vue";
import { compact, map, cloneDeep, trim } from "lodash-es";
import {
  CloseCircleIcon,
  MoveIcon,
  LinkIcon,
  InfoCircleIcon,
} from "tdesign-icons-vue-next";
import { getRelationUUID } from "@/utils";
import { t } from "@/utils/i18n";
import { validateUrl } from "@/utils/rules";

const emit = defineEmits(["updateData"]);

const props = defineProps<{
  data: {
    branches: Array<{ content: string; id: string }>;
    tip: string;
    optionType: string;
  };
}>();

type Model = {
  nodeData: {
    branches: Array<{
      content: string;
      id: string;
      urlStatus?: number;
      url?: string;
    }>;
    tip: string;
    optionType: string;
  };
};

const getLinkColor = (index) => {
  const { urlStatus, url } = formData.branches[index];
  if (formData.optionType === "0") {
    return "#99a2b2";
  }
  if (!urlStatus) {
    return "";
  }
  if (!trim(url) || !validateUrl(url)) {
    return "#E04343";
  }
  return "#1C66E5";
};

const formData = reactive<Model["nodeData"]>({
  branches: props.data.branches,
  tip: props.data.tip ?? "",
  optionType: props.data?.optionType?.toString() ?? "1",
});

const urlRule = (urlStatus) => {
  return [
    {
      validator: (str) => {
        if (!urlStatus) {
          return { result: true };
        }
        if (!trim(str)) {
          return { result: false, message: t("不能为空"), type: "error" };
        }
        if (!validateUrl(str)) {
          return {
            result: false,
            message: t("请输入正确url地址"),
            type: "error",
          };
        }
        return { result: true };
      },
      trigger: "change",
    },
  ];
};

const columns = [
  {
    colKey: "drag", // 列拖拽排序必要参数
    cell: (h) => (
      <span>
        <MoveIcon />
      </span>
    ),
    width: 20,
  },
  {
    colKey: "branch",
  },
];

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const branchListRules = [
  {
    validator: (branches) => {
      const _arr = map(compact(branches), "content");
      if (new Set(_arr).size !== _arr.length) {
        return false;
      }
      return true;
    },
    message: t("选项不能重复"),
    trigger: "change",
  },
];

const onDragSort = (params) => {
  formData.branches = cloneDeep(params.currentData);
};

const onSwitchChange = (index, v) => {
  formData.branches[index].urlStatus = v ? 1 : 0;
};

const addRelation = () => {
  formData.branches.push({
    id: getRelationUUID(),
    content: "",
  });
};

const deleteRelation = (index: number) => {
  if (formData.branches.length <= 1) return;
  formData.branches.splice(index, 1);
};

watch(formData, (newVal, oldVal) => {
  emit("updateData", {
    ...newVal,
    optionType: Number(newVal.optionType),
  });
});
</script>
<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 52px;
}
:deep(.flow-t-table__handle-draggable span) {
  vertical-align: sub;
}
.popContainer {
  padding: 16px 16px 8px;
}
.infoIcon {
  color: #99a2b2;
  margin-top: -3px;
  vertical-align: middle;
}
</style>
